/**
 * Outil « Horloge des durées » (Boîte à outils, 6e → 5e).
 *
 * On règle une heure en faisant tourner les aiguilles (au doigt ou au
 * clavier) ou avec les compteurs. « Entre deux heures » : la durée se compte
 * en sauts sur une frise (jusqu'à l'heure pile, heures entières, minutes
 * restantes), puis en soustraction posée où l'on emprunte 1 h = 60 min ; le
 * calcul « en décimaux » (10,20 − 8,45 = 1,75) est montré faux. « Heure
 * d'arrivée » : départ + durée, avec la retenue 60 min = 1 h. Calculs :
 * `durations.ts`.
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  CHALLENGES,
  DAY,
  decimalTrap,
  durationBetween,
  durationText,
  handAngles,
  hm,
  inHours,
  minuteAtAngle,
  nextDay,
  postedAddition,
  postedSubtraction,
  steps,
  timeText,
  turnHourHand,
  turnMinuteHand,
} from './durations';

type Mode = 'between' | 'add';
type Which = 'start' | 'end';

const S = 240;
const C = S / 2;
const R = 104;
const HOUR_LEN = 52;
const MIN_LEN = 82;
const START_COLOR = 'var(--color-primary-500)';
/** texte du départ : primary-600 reste lisible en thème sombre (où l'échelle s'inverse) */
const START_TEXT = 'var(--color-primary-600)';
const END_COLOR = 'var(--color-accent-700)';

const wrap = (t: number) => ((t % DAY) + DAY) % DAY;
const tip = (deg: number, len: number) => ({
  x: C + len * Math.sin((deg * Math.PI) / 180),
  y: C - len * Math.cos((deg * Math.PI) / 180),
});
const two = (m: number) => String(m).padStart(2, '0');

export function DurationClockTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const decimalHours = rank === null || rank >= 2;
  const [mode, setMode] = useState<Mode>('between');
  const [start, setStart] = useState(8 * 60 + 45);
  const [end, setEnd] = useState(10 * 60 + 20);
  const [dur, setDur] = useState(95);
  const [which, setWhich] = useState<Which>('start');
  const [challenge, setChallenge] = useState<number | null>(null);
  const [guess, setGuess] = useState({ h: 0, m: 0 });
  const [checked, setChecked] = useState<boolean | null>(null);
  const [revealed, setRevealed] = useState(false);

  const arrival = wrap(start + dur);
  const shownEnd = mode === 'between' ? end : arrival;
  const editing: Which = mode === 'add' ? 'start' : which;
  const t = editing === 'start' ? start : end;
  const setT = (v: number) => {
    setChecked(null);
    if (editing === 'start') setStart(wrap(v));
    else setEnd(wrap(v));
  };

  const d = mode === 'between' ? durationBetween(start, end) : dur;
  const jumps = steps(start, shownEnd);
  const sub = postedSubtraction(start, end);
  const trap = decimalTrap(start, end);
  const addition = postedAddition(start, dur);
  const hours = inHours(d);

  const startChallenge = (i: number) => {
    const [s, e] = CHALLENGES[i]!;
    setMode('between');
    setStart(s);
    setEnd(e);
    setChallenge(i);
    setGuess({ h: 0, m: 0 });
    setChecked(null);
    setRevealed(false);
  };
  const done = checked === true || revealed;
  // pendant un défi, départ et arrivée sont figés et la solution cachée
  const locked = challenge !== null && !done;
  const final = addition.carried ?? addition.sum;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'between', label: 'Entre deux heures' },
            { value: 'add', label: 'Heure d’arrivée' },
          ]}
          onChange={(m) => {
            setMode(m);
            setChallenge(null);
          }}
        />
        <ToggleChip
          on={challenge !== null}
          onClick={() => (challenge === null ? startChallenge(0) : setChallenge(null))}
        >
          Défi
        </ToggleChip>
      </div>

      {mode === 'between' ? (
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Heure à régler">
          {(['start', 'end'] as const).map((w) => (
            <button
              key={w}
              type="button"
              aria-pressed={which === w}
              onClick={() => setWhich(w)}
              className={cn(
                'min-h-11 rounded-2xl border px-3 py-1 text-left',
                which === w ? 'border-primary-500 bg-primary-50' : 'border-ink-200',
              )}
            >
              <span className="text-ink-500 block text-xs font-semibold">
                {w === 'start' ? 'Départ' : 'Arrivée'}
              </span>
              <span
                className="font-display block text-lg font-bold tabular-nums"
                style={{ color: w === 'start' ? START_TEXT : END_COLOR }}
                data-testid={w === 'start' ? 'dc-start' : 'dc-end'}
              >
                {timeText(w === 'start' ? start : end)}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-ink-700 text-sm">
          Départ{' '}
          <strong className="tabular-nums" style={{ color: START_TEXT }} data-testid="dc-start">
            {timeText(start)}
          </strong>{' '}
          : règle-le sur l’horloge, puis la durée.
        </p>
      )}

      <Clock
        t={t}
        ghost={mode === 'between' ? (editing === 'start' ? end : start) : null}
        color={editing === 'start' ? START_COLOR : END_COLOR}
        name={editing === 'start' ? 'départ' : 'arrivée'}
        onChange={locked ? undefined : setT}
      />

      {!locked && (
        <div className="grid grid-cols-2 gap-2">
          <Stepper
            label="Heures"
            value={hm(t).h}
            min={-1}
            max={24}
            format={(v) => `${(v + 24) % 24} h`}
            onChange={(v) => setT(t + (v - hm(t).h) * 60)}
          />
          <Stepper
            label="Minutes"
            value={hm(t).m}
            min={-1}
            max={60}
            format={(v) => `${two((v + 60) % 60)} min`}
            onChange={(v) => setT(t + (v - hm(t).m))}
          />
        </div>
      )}

      {mode === 'add' && (
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Durée">
          <Stepper
            label="Durée : heures"
            value={Math.floor(dur / 60)}
            min={0}
            max={23}
            format={(v) => `${v} h`}
            onChange={(v) => setDur(v * 60 + (dur % 60))}
          />
          <Stepper
            label="Durée : minutes"
            value={dur % 60}
            min={-1}
            max={60}
            format={(v) => `${two((v + 60) % 60)} min`}
            onChange={(v) => setDur(Math.max(0, Math.min(DAY - 1, dur + (v - (dur % 60)))))}
          />
        </div>
      )}

      {challenge !== null && (
        <section
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          aria-live="polite"
          data-testid="dc-challenge"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {challenge + 1}/{CHALLENGES.length} · Combien de temps entre {timeText(start)} et{' '}
            {timeText(end)}
            {nextDay(start, end) ? ' (le lendemain)' : ''} ?
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Stepper
              label="Ta réponse : heures"
              value={guess.h}
              min={0}
              max={23}
              format={(v) => `${v} h`}
              onChange={(v) => {
                setGuess((g) => ({ ...g, h: v }));
                setChecked(null);
              }}
            />
            <Stepper
              label="Ta réponse : minutes"
              value={guess.m}
              min={0}
              max={59}
              step={5}
              format={(v) => `${two(v)} min`}
              onChange={(v) => {
                setGuess((g) => ({ ...g, m: v }));
                setChecked(null);
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={PRIMARY_BUTTON}
              onClick={() => setChecked(guess.h * 60 + guess.m === d)}
            >
              Vérifier
            </button>
            {!done && checked !== null && (
              <button type="button" className={SECONDARY_BUTTON} onClick={() => setRevealed(true)}>
                Voir la réponse
              </button>
            )}
            {done && (
              <>
                {checked === true && <p className="text-success-600 text-sm font-bold">Juste !</p>}
                {challenge + 1 < CHALLENGES.length && (
                  <button
                    type="button"
                    className={cn(PRIMARY_BUTTON, 'ml-auto')}
                    onClick={() => startChallenge(challenge + 1)}
                  >
                    Défi suivant
                  </button>
                )}
              </>
            )}
            {checked === false && !revealed && (
              <p className="text-ink-700 text-sm">
                Pas encore : compte d’abord jusqu’à l’heure pile, puis les heures entières.
              </p>
            )}
          </div>
        </section>
      )}

      {!locked && (
        <>
          {/* une phrase courte pour le lecteur d'écran, pas tout le bloc à chaque cran */}
          <p className="sr-only" aria-live="polite">
            {mode === 'between'
              ? `Durée : ${durationText(d)}${nextDay(start, end) ? ', arrivée le lendemain' : ''}.`
              : `Arrivée : ${timeText(arrival)}${addition.days > 0 ? ', le lendemain' : ''}.`}
          </p>
          <Timeline jumps={jumps} start={start} />

          <section
            className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
            data-testid="dc-result"
          >
            {mode === 'between' ? (
              <>
                <p className="text-ink-900 text-lg font-bold">
                  Durée : {durationText(d)}
                  {d >= 60 && <span className="text-ink-600 font-semibold"> = {d} min</span>}
                </p>
                {nextDay(start, end) && (
                  <p className="text-ink-600 text-sm">
                    L’arrivée est avant le départ sur l’horloge : on arrive le lendemain.
                  </p>
                )}
              </>
            ) : (
              <p className="text-ink-900 text-lg font-bold" data-testid="dc-arrival">
                Arrivée : {timeText(arrival)}
                {addition.days > 0 && (
                  <span className="text-ink-600 font-semibold"> (le lendemain)</span>
                )}
              </p>
            )}
            {decimalHours && d > 0 && (
              <p className="text-ink-700 text-sm" data-testid="dc-hours">
                En heures : {d} min = ({d} ÷ 60) h ={' '}
                {hours.plain.includes('/')
                  ? `${hours.plain} h ≈ ${formatNumber(hours.value, 2)} h`
                  : `${hours.plain} h`}
              </p>
            )}
            {onSend && d > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-ink-600 text-sm">En minutes : {d}</span>
                <SendToSheetButton
                  label={`${d} (minutes)`}
                  cost={sendCost}
                  onClick={() => onSend({ atoms: [String(d)] })}
                />
                {decimalHours && !hours.plain.includes('/') && d % 60 !== 0 && (
                  <>
                    <span className="text-ink-600 text-sm">En heures : {hours.plain}</span>
                    <SendToSheetButton
                      label={`${hours.plain} (heures)`}
                      cost={sendCost}
                      onClick={() => onSend({ atoms: hours.atoms })}
                    />
                  </>
                )}
              </div>
            )}
          </section>

          <section aria-label="Calcul posé" className="space-y-1">
            <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
              Calcul posé
            </p>
            {mode === 'between' ? (
              <Posed
                rows={[
                  [sub.top, sub.borrowed],
                  [sub.bottom, null],
                ]}
                op="−"
                result={sub.result}
                testId="dc-posed"
              />
            ) : (
              <Posed
                rows={[
                  [hm(start), null],
                  [{ h: Math.floor(dur / 60), m: dur % 60 }, null],
                ]}
                op="+"
                result={addition.sum}
                extra={[
                  ...(addition.carried ? [addition.carried] : []),
                  ...(final.h >= 24 ? [{ h: final.h - 24, m: final.m, note: 'le lendemain' }] : []),
                ]}
                testId="dc-posed"
              />
            )}
            {mode === 'between' && sub.borrowed && (
              <p className="text-ink-600 text-sm">
                {sub.top.m} min − {sub.bottom.m} min, impossible : on prend 1 h = 60 min aux heures,{' '}
                {sub.top.h} h {two(sub.top.m)} min devient {sub.borrowed.h} h {sub.borrowed.m} min.
              </p>
            )}
            {mode === 'between' && sub.top.h >= 24 && (
              <p className="text-ink-600 text-sm">
                Arrivée le lendemain : on ajoute 24 h, {timeText(end)} devient {sub.top.h} h{' '}
                {two(sub.top.m)}.
              </p>
            )}
            {mode === 'add' && addition.carried && (
              <p className="text-ink-600 text-sm">
                {addition.sum.m} min = 1 h {two(addition.sum.m - 60)} min : on ajoute 1 h aux
                heures.
              </p>
            )}
            {mode === 'add' && final.h >= 24 && (
              <p className="text-ink-600 text-sm">
                {final.h} h dépasse minuit (24 h) : on enlève 24 h, on arrive le lendemain à{' '}
                {timeText(arrival)}.
              </p>
            )}
          </section>

          {mode === 'between' && trap && (
            <Note live={false}>
              {`Le piège : ${trap.text}, puis «\u00a0${trap.wrong.replace(',', ' h ')} min\u00a0» ? Une heure fait 60 minutes, pas 100 : la bonne durée est ${durationText(d)}.`}
            </Note>
          )}
        </>
      )}
    </div>
  );
}

/** L'horloge : aiguilles à tourner au doigt (ou au clavier), aiguilles fantômes de l'autre heure. */
function Clock({
  t,
  ghost,
  color,
  name,
  onChange,
}: {
  t: number;
  ghost: number | null;
  color: string;
  name: string;
  /** absent : horloge figée (défi en cours) */
  onChange?: (t: number) => void;
}) {
  const dragging = useRef<'hour' | 'minute' | null>(null);
  const a = handAngles(t);
  const g = ghost === null ? null : handAngles(ghost);
  const hTip = tip(a.hour, HOUR_LEN);
  const mTip = tip(a.minute, MIN_LEN);

  const view = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const x = ((e.clientX - r.left) / r.width) * S;
    const y = ((e.clientY - r.top) / r.height) * S;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y, deg: (Math.atan2(x - C, C - y) * 180) / Math.PI };
  };
  const move = (hand: 'hour' | 'minute', deg: number) => {
    if (!onChange) return;
    if (hand === 'minute') onChange(turnMinuteHand(t, minuteAtAngle(deg)));
    else onChange(turnHourHand(t, deg));
  };
  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    const p = view(e);
    if (!p) return;
    const dH = Math.hypot(p.x - hTip.x, p.y - hTip.y);
    const dM = Math.hypot(p.x - mTip.x, p.y - mTip.y);
    const hand =
      Math.min(dH, dM) < 30
        ? dH < dM
          ? 'hour'
          : 'minute'
        : Math.hypot(p.x - C, p.y - C) < (HOUR_LEN + MIN_LEN) / 2
          ? 'hour'
          : 'minute';
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragging.current = hand;
    move(hand, p.deg);
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    if (!dragging.current) return;
    const p = view(e);
    if (p) move(dragging.current, p.deg);
  };
  const onUp = () => {
    dragging.current = null;
  };
  const keys = (unit: number) => (e: KeyboardEvent<SVGGElement>) => {
    const k = e.key;
    const delta =
      k === 'ArrowUp' || k === 'ArrowRight'
        ? unit
        : k === 'ArrowDown' || k === 'ArrowLeft'
          ? -unit
          : 0;
    if (!delta) return;
    e.preventDefault();
    onChange?.(t + delta);
  };

  return (
    <div className="mx-auto w-full max-w-[16rem]">
      <svg
        viewBox={`0 0 ${S} ${S}`}
        className={cn('block w-full touch-none select-none rounded-full', FIGURE_BG_CLASS)}
        aria-label={`Horloge réglée sur ${timeText(t)} (${name}).`}
        role="group"
        data-testid="dc-clock"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <circle
          cx={C}
          cy={C}
          r={R + 10}
          fill="none"
          stroke="var(--color-ink-200)"
          strokeWidth={3}
        />
        {Array.from({ length: 60 }, (_, i) => {
          const big = i % 5 === 0;
          const p1 = tip(i * 6, R);
          const p2 = tip(i * 6, big ? R - 10 : R - 5);
          return (
            <line
              key={i}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke={big ? 'var(--color-ink-700)' : 'var(--color-ink-300)'}
              strokeWidth={big ? 2.5 : 1.25}
            />
          );
        })}
        {Array.from({ length: 12 }, (_, i) => {
          const p = tip((i + 1) * 30, R - 24);
          return (
            <text
              key={i}
              x={p.x}
              y={p.y + 5}
              textAnchor="middle"
              fontSize={15}
              fontWeight={600}
              fill="var(--color-ink-700)"
            >
              {i + 1}
            </text>
          );
        })}
        {g && (
          <g opacity={0.3} aria-hidden>
            <line
              x1={C}
              y1={C}
              x2={tip(g.hour, HOUR_LEN).x}
              y2={tip(g.hour, HOUR_LEN).y}
              stroke="var(--color-ink-700)"
              strokeWidth={6}
              strokeLinecap="round"
            />
            <line
              x1={C}
              y1={C}
              x2={tip(g.minute, MIN_LEN).x}
              y2={tip(g.minute, MIN_LEN).y}
              stroke="var(--color-ink-700)"
              strokeWidth={4}
              strokeLinecap="round"
            />
          </g>
        )}
        <g
          role="slider"
          tabIndex={0}
          aria-disabled={!onChange || undefined}
          aria-label={`Aiguille des heures (${name})`}
          aria-valuemin={0}
          aria-valuemax={23}
          aria-valuenow={hm(t).h}
          aria-valuetext={timeText(t)}
          onKeyDown={keys(60)}
          className="cursor-grab outline-none [&:focus-visible_.ring]:opacity-100"
        >
          <line
            x1={C}
            y1={C}
            x2={hTip.x}
            y2={hTip.y}
            stroke="var(--color-ink-900)"
            strokeWidth={7}
            strokeLinecap="round"
          />
          <circle cx={hTip.x} cy={hTip.y} r={9} fill="var(--color-ink-900)" />
          <circle
            className="ring"
            cx={hTip.x}
            cy={hTip.y}
            r={15}
            fill="none"
            stroke="var(--color-primary-400)"
            strokeWidth={2.5}
            opacity={0}
          />
        </g>
        <g
          role="slider"
          tabIndex={0}
          aria-disabled={!onChange || undefined}
          aria-label={`Aiguille des minutes (${name})`}
          aria-valuemin={0}
          aria-valuemax={59}
          aria-valuenow={hm(t).m}
          aria-valuetext={timeText(t)}
          onKeyDown={keys(1)}
          className="cursor-grab outline-none [&:focus-visible_.ring]:opacity-100"
        >
          <line
            x1={C}
            y1={C}
            x2={mTip.x}
            y2={mTip.y}
            stroke={color}
            strokeWidth={4.5}
            strokeLinecap="round"
          />
          <circle cx={mTip.x} cy={mTip.y} r={10} fill={color} />
          <circle
            className="ring"
            cx={mTip.x}
            cy={mTip.y}
            r={16}
            fill="none"
            stroke="var(--color-primary-400)"
            strokeWidth={2.5}
            opacity={0}
          />
        </g>
        <circle cx={C} cy={C} r={6} fill="var(--color-ink-900)" />
      </svg>
    </div>
  );
}

/** La frise des sauts : départ, heures piles, arrivée. */
function Timeline({ jumps, start }: { jumps: ReturnType<typeof steps>; start: number }) {
  const W = 320;
  const H = 86;
  const PAD = 30;
  const Y = 44;
  if (jumps.length === 0)
    return <p className="text-ink-600 text-sm">Départ et arrivée à la même heure : durée nulle.</p>;
  const w = (W - 2 * PAD) / jumps.length;
  const xs = [PAD, ...jumps.map((_, i) => PAD + (i + 1) * w)];
  const times = [start, ...jumps.map((j) => j.to)];
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={`Frise : ${jumps
          .map((j) => `de ${timeText(j.from)} à ${timeText(j.to)}, ${durationText(j.minutes)}`)
          .join(' ; ')}.`}
        data-testid="dc-timeline"
      >
        <line x1={PAD} x2={W - PAD} y1={Y} y2={Y} stroke="var(--color-ink-300)" strokeWidth={3} />
        {jumps.map((j, i) => {
          const x1 = xs[i]!;
          const x2 = xs[i + 1]!;
          const mid = (x1 + x2) / 2;
          return (
            <g key={i}>
              <path
                d={`M${x1 + 4},${Y - 4} Q${mid},${Y - 30} ${x2 - 4},${Y - 4}`}
                fill="none"
                stroke={j.kind === 'hours' ? 'var(--color-ink-700)' : START_COLOR}
                strokeWidth={2}
              />
              <HaloText
                x={mid}
                y={Y - 22}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill="var(--color-ink-900)"
              >
                {`+ ${durationText(j.minutes)}`}
              </HaloText>
            </g>
          );
        })}
        {xs.map((x, i) => (
          <g key={i}>
            <circle
              cx={x}
              cy={Y}
              r={5}
              fill={
                i === 0 ? START_COLOR : i === xs.length - 1 ? END_COLOR : 'var(--color-ink-700)'
              }
            />
            <text
              x={x}
              y={Y + 22}
              textAnchor="middle"
              fontSize={12}
              fontWeight={i === 0 || i === xs.length - 1 ? 700 : 500}
              fill="var(--color-ink-700)"
            >
              {timeText(times[i]!)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/** Calcul posé en colonnes h | min, avec l'emprunt ou la retenue, puis les lignes qui suivent. */
function Posed({
  rows,
  op,
  result,
  extra = [],
  testId,
}: {
  rows: Array<[{ h: number; m: number }, { h: number; m: number } | null]>;
  op: '+' | '−';
  result: { h: number; m: number };
  extra?: Array<{ h: number; m: number; note?: string }>;
  testId: string;
}) {
  const cell = 'px-2 text-right tabular-nums';
  return (
    <table className="text-ink-900 font-display text-lg" data-testid={testId}>
      <tbody>
        {rows.map(([v, b], i) => (
          <tr key={i}>
            <td className="text-ink-500 w-6 pr-1 text-right">{i === rows.length - 1 ? op : ''}</td>
            <td className={cell}>
              {b ? (
                <>
                  <s className="text-ink-400">{v.h}</s> {b.h}
                </>
              ) : (
                v.h
              )}{' '}
              h
            </td>
            <td className={cell}>
              {b ? (
                <>
                  <s className="text-ink-400">{two(v.m)}</s> {b.m}
                </>
              ) : (
                two(v.m)
              )}{' '}
              min
            </td>
          </tr>
        ))}
        <tr className="border-ink-700 border-t-2">
          <td className="text-ink-500 w-6 pr-1 text-right">=</td>
          <td className={cn(cell, 'font-bold')}>{result.h} h</td>
          <td className={cn(cell, 'font-bold')}>{two(result.m)} min</td>
        </tr>
        {extra.map((x, i) => (
          <tr key={i}>
            <td className="text-ink-500 w-6 pr-1 text-right">=</td>
            <td className={cn(cell, 'font-bold')}>{x.h} h</td>
            <td className={cn(cell, 'font-bold')}>
              {two(x.m)} min
              {x.note && <span className="text-ink-600 text-sm font-semibold"> ({x.note})</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
